import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { QuickPayService } from '../../services/quickpay.service';
import { ApplicationService } from '../../services/application.service';
import { CompanyService } from '../../services/company.service';

import { QuickPayInfo, QuickPayRequest, QuickPayUserInformation } from '../../models/quickpay';
import { MessageListItem } from '../../models/application';
import { VerifyService } from 'src/app/services/verify.service';
import { AcctTypeListItem } from '../../models/reference';

@Component({
  selector: 'app-quick-pay-login',
  templateUrl: './quick-pay-login.component.html',
  styleUrls: ['./quick-pay-login.component.scss']
})
export class QuickPayComponent implements OnInit {

  private _handleError: HandleError;
  private _logging = environment.logging;
  recaptchaSiteKey = environment.restAPI.recaptchaSiteKey;

  // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showWelcomeMessagesSpinner = false;
  showTemporaryMessagesSpinner = false;
  showGeneralMessagesSpinner = false;             //01/10/2023 - 1098-E Message

  showLoginTimeoutError = false;

  welcomeMessageList: MessageListItem[] = [];
  temporaryMessageList: MessageListItem[] = [];
  generalMessageList: MessageListItem[] = [];     //01/10/2023 - 1098-E Message

  quickpayForm: UntypedFormGroup;
  partiFormControl = new UntypedFormControl();
  lnameFormControl = new UntypedFormControl();
  zipFormControl = new UntypedFormControl();
  recaptchaFormControl = new UntypedFormControl();

  quickpayRequest: QuickPayRequest;
  quickpayUserInformation: QuickPayUserInformation;

  // 10/21/2024 RA
  getAcctTypeListComplete = false;
  AcctTypeList: AcctTypeListItem[] = [];
  showAcctTypListSpinner = false;

  partiEnabled = true;
  lnameEnabled = true;
  zipEnabled = true;
  partiVisible = true;
  lnameVisible = true;
  zipVisible = true;

  userNameEnabled = true;
  userNameVisible = true;
  submitButtonVisible = true;
  showLockedOutError = false;
  showTemporaryPasswordError = false;
  showExpiredTemporaryPasswordError = false;

  showVerifyPartiError = false;
  showLoginError = false;
  showStdNumPartiError = false;
  showNoteCountError = false;

  verifyVisible = true;

  showLoginSpinner = false;
  selected: string;
  arr: string[] = [];

  quickpayInfo: QuickPayInfo;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _quickpayService: QuickPayService,
    private _verifyService: VerifyService,
    private _applicationService: ApplicationService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this.arr.push("Account Id");
    this.arr.push("Student Number")
    this.selected = 'acctId';

    this._handleError = _errorHandler.createHandleError('QuickPayComponent');

    this.quickpayRequest = this._quickpayService.getNewQuickPayRequest();
    this.quickpayUserInformation = this._quickpayService.getNewQuickPayUserInformation();

    this.getData();
    this.createForm();
    this.scrollToTop();
  }

  ngOnInit() {
    this._quickpayService.quickPayInfo.subscribe(response => this._onUserInfoChanged(response), error => this._handleError(error));
  }

  getData(): void {
    const companyCode: string = this._companyService.getCompanyCode();

    this.showWelcomeMessagesSpinner = true;
    this.getWelcomeMessageList(companyCode);

    this.showTemporaryMessagesSpinner = true;
    this.getTemporaryMessageList(companyCode);

    //01/10/2023 - 1098-E Message
    this.showGeneralMessagesSpinner = true;
    this.getGeneralMessageList(companyCode);
  }

  getWelcomeMessageList(companyCode: string): void {
    this.showWelcomeMessagesSpinner = true;
    this._applicationService.getWelcomeMessageList(companyCode).subscribe(
      resultArray => {
        this.welcomeMessageList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('QuickPayComponent.getWelcomeMessageList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('QuickPayComponent.getWelcomeMessageList :: result = ' + JSON.stringify(resultArray));
        }
        this.showWelcomeMessagesSpinner = false;
      },
      error => this._handleError('getWelcomeMessageList', error)
    );
  }

  getTemporaryMessageList(companyCode: string): void {
    this.showTemporaryMessagesSpinner = true;
    this._applicationService.getTemporaryMessageList(companyCode).subscribe(
      resultArray => {
        this.temporaryMessageList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('QuickPayComponent.getTemporaryMessageList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('QuickPayComponent.getTemporaryMessageList :: result = ' + JSON.stringify(resultArray));
        }
        this.showTemporaryMessagesSpinner = false;
      },
      error => this._handleError('getTemporaryMessageList', error)
    );
  }

  //01/10/2023 - 1098-E Message
  getGeneralMessageList(companyCode: string): void {
    this.showGeneralMessagesSpinner = true;
    this._applicationService.getGeneralMessageList(companyCode,"generalBold").subscribe(
      resultArray => {
        this.generalMessageList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('QuickPayComponent.getGeneralMessageList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('QuickPayComponent.getGeneralMessageList :: result = ' + JSON.stringify(resultArray));
        }
        this.showGeneralMessagesSpinner = false;
      },
      error => this._handleError('getGeneralMessageList', error)
    );
  }

  getMessageCssClass(item: MessageListItem): string {
    return this._applicationService.getMessageListItemDisplayClass(item);
  }

  createForm(): void {
    this.quickpayForm = this._formBuilder.group({});
    this.quickpayForm.addControl('partiFormControl', this.partiFormControl);
    this.quickpayForm.addControl('lnameFormControl', this.lnameFormControl);
    this.quickpayForm.addControl('zipFormControl', this.zipFormControl);
    this.quickpayForm.addControl('recaptchaFormControl', this.recaptchaFormControl);

    this.quickpayForm.setValue({
      partiFormControl: null,
      lnameFormControl: null,
      zipFormControl: null,
      recaptchaFormControl: null
    });

    this.quickpayForm.get('partiFormControl').setValidators([Validators.required]);
    this.quickpayForm.get('lnameFormControl').setValidators([Validators.required]);
    this.quickpayForm.get('zipFormControl').setValidators([Validators.required]);
    this.quickpayForm.get('recaptchaFormControl').setValidators([Validators.required]);

    this.quickpayForm.get('partiFormControl').updateValueAndValidity();
    this.quickpayForm.get('lnameFormControl').updateValueAndValidity();
    this.quickpayForm.get('zipFormControl').updateValueAndValidity();
  }

  resetForm(): void {
    this.quickpayForm.setValue({
      selected: 'acctId',
      partiFormControl: '',
      lnameFormControl: '',
      zipFormControl: '',
      recaptchaFormControl: null
    });

    this.quickpayForm.controls['partiFormControl'].markAsPristine();
    this.quickpayForm.controls['lnameFormControl'].markAsPristine();
    this.quickpayForm.controls['zipFormControl'].markAsPristine();
    this.quickpayForm.controls['recaptchaFormControl'].markAsPristine();
    this.quickpayForm.controls['partiFormControl'].markAsUntouched();
    this.quickpayForm.controls['lnameFormControl'].markAsUntouched();
    this.quickpayForm.controls['zipFormControl'].markAsUntouched();
    this.quickpayForm.controls['recaptchaFormControl'].markAsUntouched();
    this.quickpayForm.get('partiFormControl').updateValueAndValidity();
    this.quickpayForm.get('lnameFormControl').updateValueAndValidity();
    this.quickpayForm.get('zipFormControl').updateValueAndValidity();
    this.quickpayForm.get('recaptchaFormControl').updateValueAndValidity();

    this.showLoginSpinner = false;

    this.partiEnabled = true;
    this.lnameEnabled = true;
    this.zipEnabled = true;
    this.partiVisible = true;
    this.lnameVisible = true;
    this.zipVisible = true;
    this.submitButtonVisible = true;
    this.showVerifyPartiError = false;
    this.showLoginError = false;
    this.showLockedOutError = false;
    this.showTemporaryPasswordError = false;
    this.showExpiredTemporaryPasswordError = false;

    this.verifyVisible = true;
  }

  loginUserEnabled(): boolean {
    return this.submitButtonVisible && this.quickpayForm.get('partiFormControl').valid && this.quickpayForm.get('lnameFormControl').valid &&
           this.quickpayForm.get('zipFormControl').valid && this.quickpayForm.get('recaptchaFormControl').valid;
  }

  submitUser(): void {
    if (this.quickpayForm.get('partiFormControl').valid && this.quickpayForm.get('lnameFormControl').valid && this.quickpayForm.get('zipFormControl').valid &&
    this.quickpayForm.get('recaptchaFormControl').valid) {
      this.showLoginSpinner = true;
      this.showLoginError = false;

      this.quickpayRequest.acct_type = this.selected;
      this.quickpayRequest.partiId = this.quickpayForm.get('partiFormControl').value;
      this.quickpayRequest.lastName= this.quickpayForm.get('lnameFormControl').value;
      this.quickpayRequest.zipCode = this.quickpayForm.get('zipFormControl').value;

      this._quickpayService.verifyPartiLnameZip(this.quickpayRequest).subscribe(
        (result) => {
          if (this._logging.serviceCallTraceLog) {
            console.log(
              "QuickPayComponent.submitUser.verifyPartiLnameZip :: result.participantId = " +
                result.partiId
            );
          }
          if (this._logging.serviceDataTraceLog) {
            console.log(
              "QuickPayComponent.submitUser.verifyPartiLnameZip :: result = " +
                JSON.stringify(result)
            );
          }

          this.showLoginSpinner = false;
          this.quickpayUserInformation = result;

          if (this.quickpayUserInformation.stdNum_count > 1) {
            this.showLoginSpinner = false;
            this.showLoginError = false;
            this.showStdNumPartiError = true;
          } else {
            if (this.quickpayUserInformation.note_count == 0) {
              this.showLoginSpinner = false;
              this.showLoginError = false;
              this.showNoteCountError = true;
            } else {
              if (
                this.quickpayUserInformation !== null &&
                this.quickpayUserInformation.partiId !== null &&
                this.quickpayUserInformation.partiId > 0 &&
                this.quickpayUserInformation.lastName !== null &&
                this.quickpayUserInformation.zipCode !== null
              ) {
                // Set Login Information
                this._quickpayService.login(
                  this.quickpayUserInformation.partiId,
                  this.quickpayUserInformation.firstName,
                  this.quickpayUserInformation.middleInitial,
                  this.quickpayUserInformation.lastName,
                  this.quickpayUserInformation.zipCode,
                  this.quickpayUserInformation.quickPayToken,
                  this.quickpayUserInformation.quickPayTokenExpiresAtUtc
                );
                this.showLoginError = false;
                this._openQuickPay_Payment();
              } else {
                // User could not be verified
                this._quickpayService.logout();
                this.showLoginSpinner = false;
                this.showLoginError = true;
              }
            }
          }
        },
        (error) => {
          this._quickpayService.logout();
          this._handleError("verifyPartiLnameZip", error, this.quickpayRequest);
          this.showLoginSpinner = false;
          this.showLoginError = true;
        }
      );
    } else {
      this.showLoginSpinner = false;
      this.showLoginError = true;
    }
  }

  submitUserEnabled(): boolean {
    return this.quickpayForm.get('QuickPayFormControl').valid;
  }

  cancelLogin(): void {
    this._openHome();
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }

  private _onUserInfoChanged(newUserInfo: QuickPayInfo): void {
    this.showLoginTimeoutError = newUserInfo.sessionTimeout;
    if (this.showLoginSpinner && newUserInfo.authenticated) {
      this.showLoginSpinner = false;
      this._openHome();
    }
  }

  private _openQuickPay_Payment(): void {
    if (this._logging.navigationTraceLog) {
      console.log('QuickPayComponent._openQuickPay_Payment');
    }
    this._router.navigate(['quickpay_pay']);
  }

  private _openHome(): void {
    if (this._logging.navigationTraceLog) {
      console.log('QuickPayComponent._openHome');
    }
    this._router.navigate(['login']);
  }
}
